import 'react-image-crop/dist/ReactCrop.css';

import JoditEditor from 'jodit-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import PhotoModalTop from '../../common/PhotoModalTop';
import PhotoModal from '../../common/PhotosModal';
import Modal from '../../Modal';
import CropModal from './CropModal';

import type { ImageKey, ImageWithType } from '../../../types.ts/general';
import type { Jodit } from 'jodit-react';

const BlogEditor: React.FC<{
  content: string;
  onContentUpdate: (html: string) => void;
  onEditorDirty?: () => void;
  disabled?: boolean;
  contentType?: 'blog' | 'socialPost';
}> = ({
  content,
  onContentUpdate,
  onEditorDirty,
  disabled,
  contentType = 'blog',
}) => {
  const editor = useRef<Jodit>(null);
  const [cropModalOpen, setCropModalOpen] = useState<boolean>(false);
  const [openMedia, toggleMedia] = useState<boolean>(false);
  const [croppedImageUrl, setCroppedImageUrl] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<HTMLElement | null>(null);
  const [selectedImage, setSelectedImage] = useState<
    ImageWithType[ImageKey] | null
  >(null);
  const [isInsertingNewImage, setIsInsertingNewImage] =
    useState<boolean>(false);

  useEffect(() => {
    const handleButtonClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'IMG') {
        setSelectedNode(target);
        const src = target.getAttribute('src') || '';
        setCroppedImageUrl(src);
      }

      if (target.closest('.jodit-toolbar-button_pencil')) {
        toggleMedia(true);
      }
    };

    document.addEventListener('click', handleButtonClick);
    return () => {
      document.removeEventListener('click', handleButtonClick);
    };
  }, []);

  useEffect(() => {
    const tooltip = document.querySelector(
      '.jodit-popup__content',
    ) as HTMLElement | null;
    if (tooltip) {
      tooltip.style.display = cropModalOpen ? 'none' : 'block';
    }
  }, [cropModalOpen]);

  const replaceImageUrl = (imgNode: HTMLImageElement, newUrl: string): void => {
    const doc = new window.DOMParser().parseFromString(content, 'text/html');
    const docImages = doc.getElementsByTagName('img');
    for (let i = 0; i < docImages.length; i++) {
      const docImg = docImages[i];
      if (docImg.src !== imgNode.src) {
        continue;
      }

      docImg.setAttribute('src', newUrl);
      docImg.removeAttribute('width');
      docImg.removeAttribute('height');
      docImg.setAttribute('data-skip-replacement', 'true');
      onContentUpdate(doc.body.innerHTML);

      break;
    }
  };

  const handleReplaceAction = () => {
    if (isInsertingNewImage && croppedImageUrl) {
      // Insert new image at cursor position
      if (editor.current) {
        const imgHtml = `<img src="${croppedImageUrl}" alt="${selectedImage?.alt || ''}" data-skip-replacement="true" />`;
        editor.current.s.insertHTML(imgHtml);
        onContentUpdate(editor.current.value);
      }
      toggleMedia(false);
      setIsInsertingNewImage(false);
      setCroppedImageUrl(null);
      setSelectedImage(null);
    } else if (selectedNode && croppedImageUrl) {
      replaceImageUrl(selectedNode as HTMLImageElement, croppedImageUrl);
      toggleMedia(false);
    }
  };

  const handleOpenMediaLibrary = () => {
    setIsInsertingNewImage(true);
    setSelectedNode(null);
    setCroppedImageUrl(null);
    setSelectedImage(null);
    toggleMedia(true);
  };

  const handleDoubleClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.tagName === 'IMG') {
      setSelectedNode(target);
      setCropModalOpen(true);
    }
  };

  const handleSaveCroppedImage = (croppedImageUrl: string) => {
    if (selectedNode) {
      replaceImageUrl(selectedNode as HTMLImageElement, croppedImageUrl);
      setCroppedImageUrl(croppedImageUrl);
      setCropModalOpen(false);
    }
  };

  // Normalize content for social posts: convert P tags to BR tags
  const normalizedContent = useMemo(() => {
    if (contentType === 'socialPost' && content) {
      // Replace closing P tags with double BR tags to preserve paragraph breaks, then remove all P tags
      return content
        .replace(/<\/p>/gi, '<br><br>')
        .replace(/<p[^>]*>/gi, '')
        .replace(/(<br>\s*){3,}/gi, '<br><br>'); // Prevent more than 2 consecutive BRs
    }
    return content;
  }, [content, contentType]);

  const config = useMemo(
    () => ({
      readonly: Boolean(disabled),
      toolbar: false,
      statusbar: false,
      placeholder: 'Start typing...',
      disablePlugins: 'image-properties,speech-recognize,ai-assistant,print',
      events: {
        dblclick: handleDoubleClick,
        keydown: (event: KeyboardEvent) => {
          // Prevent browser's default save dialog on Ctrl+S or Cmd+S
          if ((event.ctrlKey || event.metaKey) && event.key === 's') {
            event.preventDefault();
            event.stopPropagation();
            return false;
          }
        },
      },
      // Hide toolbar for social posts since social networks don't support rich text formatting
      toolbarAdaptive: contentType === 'socialPost' ? false : true,
      showCharsCounter: false,
      showWordsCounter: false,
      showXPathInStatusbar: false,
      // Only show the main toolbar row, not extra rows
      toolbarButtonSize: 'middle' as const,
      // Custom button for inserting images from media library
      controls: {
        insertMediaImage: {
          name: 'insertMediaImage',
          icon: 'image',
          tooltip: 'Insert image from media library',
          exec: () => {
            handleOpenMediaLibrary();
          },
        },
      },
      buttons:
        contentType === 'socialPost'
          ? []
          : [
              'bold',
              'italic',
              'underline',
              'strikethrough',
              '|',
              'ul',
              'ol',
              '|',
              'brush',
              '|',
              'paragraph',
              '|',
              'left',
              'center',
              'right',
              'justify',
              '|',
              'link',
              'insertMediaImage',
              '|',
              'undo',
              'redo',
            ],
      // Remove unwanted buttons from toolbar
      removeButtons: [
        'speech-recognize',
        'ai-assistant',
        'print',
        'about',
        'classSpan',
        'copyformat',
        'dots',
        'image',
      ],
      // For social posts, use BR tags instead of P tags to eliminate spacing
      enter: (contentType === 'socialPost' ? 'br' : 'p') as 'br' | 'p',
      // Remove paragraph spacing for social posts to match LinkedIn behavior
      style:
        contentType === 'socialPost'
          ? {
              '.jodit-wysiwyg p': {
                margin: '0 !important',
                marginTop: '0 !important',
                marginBottom: '0 !important',
                padding: '0 !important',
                display: 'inline',
              },
              '.jodit-wysiwyg': {
                lineHeight: '1.5',
                backgroundColor: 'var(--background)',
                color: 'var(--foreground)',
              },
            }
          : {
              '.jodit-wysiwyg': {
                backgroundColor: 'var(--background)',
                color: 'var(--foreground)',
              },
              '.jodit-wysiwyg h1, .jodit-wysiwyg h2, .jodit-wysiwyg h3': {
                color: 'var(--foreground)',
                lineHeight: '1.3',
              },
              '.jodit-wysiwyg blockquote': {
                color: 'var(--content-editor-foreground)',
                borderLeft: '3px solid var(--content-editor-foreground)',
                paddingLeft: '16px',
                fontStyle: 'italic',
              },
              '.jodit-wysiwyg a': {
                color: 'var(--foreground)',
              },
            },
    }),
    [disabled, contentType],
  );

  return (
    <>
      <JoditEditor
        data-testid="article-editor"
        ref={editor}
        value={normalizedContent}
        config={config}
        onBlur={newContent => onContentUpdate(newContent)}
        onChange={() => onEditorDirty?.()}
      />

      {openMedia && (
        <Modal
          isOpen={true}
          onClose={() => {
            toggleMedia(false);
            setIsInsertingNewImage(false);
          }}
          paddingHorizontal="0"
        >
          <PhotoModal
            TopComponent={props => (
              <PhotoModalTop
                {...props}
                title={isInsertingNewImage ? 'Insert Image' : 'blog'}
                isSelected={!!croppedImageUrl}
                replaceAction={handleReplaceAction}
                selectedImage={selectedImage}
              />
            )}
            otherFields={['stock']}
            onCloseSelf={() => {
              toggleMedia(false);
              setIsInsertingNewImage(false);
            }}
            selectedImageUrl={selectedImage?.url || null}
            setSelectedImage={img => {
              if (!img) return;
              setCroppedImageUrl(img.url);
              setSelectedImage(img);
            }}
          />
        </Modal>
      )}

      {cropModalOpen && croppedImageUrl && (
        <CropModal
          isOpen={cropModalOpen}
          onClose={() => setCropModalOpen(false)}
          selectedImage={croppedImageUrl}
          onSave={handleSaveCroppedImage}
        />
      )}
    </>
  );
};

export default BlogEditor;
